import { useEffect, useState } from "react";
import { AddCircleOutline } from "@mui/icons-material";
import { Grid, IconButton, Tooltip } from "@mui/material";

import Box from "../Box";
import Modal from "../Modal";
import Button from "../Button";
import FormField from "../FormField";
import EmailInput from "../EmailInput";
import Typography from "../Typography";
import Autocomplete from "../Autocomplete";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import ModalCreateUpdateGroup from "../Group/modalCreateUpdateGroup";
import useContainerInvitation from "../../hooks/useContainerInvitation";

import { ICodename, ILableOrganization } from "../../commons/interfaces";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";
import { ICommunitiesByInvitation, ICreateInvitation, IUserResourcesByInvitation } from "../../services/identity/invitation.service";
import { RoleHelpers } from "../../commons/roleHelpers";



interface IErrorInvite {
    roles?: string,
    emails?: string,
    groupIds?: string,

    note?: string,
    communities?: { [key in keyof ICommunitiesByInvitation]?: string }[],
    userResources?: { [key in keyof IUserResourcesByInvitation]?: string }[],
}

interface IPropsModalInvitationMember {
    newDomain?: string;
    groupId?: string;
    roleList: ICodename[];
    groupList: ICodename[];
    requiredRole?: boolean;
    organizationId: string;
    onCallBack: () => void;
}

const ModalInvitationMember = (props: IPropsModalInvitationMember & ILableOrganization) => {
    const strings = useStrings();

    const { onCreateInvitation } = useContainerInvitation();

    const {
        clientId,
        language,
        tenantCode,
    } = useCommonComponentContext();

    const [visible, setVisible] = useState(false);

    const [groupList, setGroupList] = useState<ICodename[]>([]);
    const [openCreateGroup, setOpenCreateGroup] = useState<boolean>(false);

    const [errorModal, setErrorModal] = useState<IErrorInvite>({});
    const [dataModal, setDataModal] = useState<ICreateInvitation | undefined>(undefined);

    useEffect(() => { setGroupList(props.groupList); }, [props.groupList]);

    const onChangeValue = (val: any, key: string) => {
        setDataModal(prev => ({
            ...prev,
            [key]: val,
        }));

        setErrorModal(prev => ({
            ...prev,
            [key]: undefined,
        }));
    };

    const handleActionModal = async () => {
        let checked: boolean = true;
        let newError: any = { ...errorModal };

        if ([...dataModal?.emails || []].length === 0) {
            checked = false;
            newError["emails"] = strings.INVITATION.REQUIRED_EMAIL;
        }

        if (
            props.requiredRole &&
            (Helpers.isNullOrEmpty(dataModal?.roles) || [...dataModal?.roles].length === 0)
        ) {
            checked = false;
            newError["roles"] = strings.Common.REQUIRED;
        }

        if (!checked) {
            setErrorModal(newError);
        } else {
            const newValueGroupIds = Helpers.isNullOrEmpty(props.groupId)
                ? [...dataModal?.groupIds || []]
                : [props.groupId];

            onCreateInvitation({
                newDomain: props.newDomain,

                onCallBack: async () => {
                    setVisible(false);
                    setDataModal(undefined);
                    setErrorModal(undefined);

                    await props.onCallBack();
                },

                data: {
                    note: dataModal?.note,
                    roles: dataModal?.roles,
                    emails: dataModal?.emails,
                    groupIds: newValueGroupIds,

                    clientId: clientId,
                    tenantCode: tenantCode,
                    organizationId: props.organizationId,
                },
            });
        }
    };

    return (
        <>
            <Button
                color="info"
                onClick={() => {
                    setVisible(true);
                    setDataModal(undefined);
                    setErrorModal(undefined);
                }}>
                {strings.format(strings.Staff.INVITATION_STAFF, (props.lableStaff || strings.Common.STAFF).toLowerCase())}
            </Button>
            <Modal
                fullWidth
                maxWidth="sm"
                hasActionButton
                visible={visible}
                title={strings.Staff.CREATE_INVITATION}
                buttonAction={strings.INVITATION.SEND_INVITATION}
                onClose={() => {
                    setVisible(false);
                    setDataModal(undefined);
                    setErrorModal(undefined);
                }}
                onAction={() => {
                    handleActionModal();
                }}>
                <Grid container spacing={3} p={2}>
                    <Grid item xs={12}>
                        {/* List Email invatation */}
                        <EmailInput
                            required
                            maxEmails={255}
                            variant="standard"
                            label={strings.Staff.EMAIL}
                            placeholder={strings.Staff.ENTER_EMAIL}
                            value={dataModal?.emails || []}
                            errorMessage={errorModal?.emails}
                            onChangeValue={value => {
                                onChangeValue(value, "emails");
                            }}
                        />
                        <Typography variant="button" color="secondary">{strings.INVITATION.NOTE_EMAIL_INVITATION}</Typography>
                    </Grid>

                    {/* Message invatation */}
                    <Grid item xs={12}>
                        <FormField
                            multiline
                            minRows={3}
                            value={dataModal?.note}
                            errorMessage={errorModal?.note}
                            label={strings.INVITATION.MESSAGE}
                            placeholder={strings.INVITATION.ENTER_MESSAGE}
                            onChangeValue={(value: any) => { onChangeValue(value, "note"); }}
                        />
                        <Typography variant="button" color="secondary">{strings.INVITATION.NOTE_MESSAGE_INVITATION}</Typography>
                    </Grid>

                    {/* Role invatation */}
                    <Grid item xs={12}>
                        <Box mb={2}>
                            <Typography variant="h6">{strings.INVITATION.ROLE}</Typography>
                        </Box>
                        <Autocomplete
                            multiple
                            label={strings.Staff.ROLE}
                            data={props.roleList || []}
                            required={props.requiredRole}
                            defaultValue={dataModal?.roles}
                            errorMessage={errorModal?.roles}
                            placeholder={strings.Staff.SELECT_ROLE}
                            optionDisabled={
                                [...props.roleList || []].filter(el => (
                                    RoleHelpers.isOrganizationOwner(el.detail?.roleType)
                                ))?.map(e => `${e.code}`) || []
                            }
                            onChange={(value: any) => onChangeValue(value, "roles")}
                        />
                    </Grid>

                    {/* Group invatation */}
                    {Helpers.isNullOrEmpty(props.groupId) &&
                        <Grid item xs={12}>
                            <Box sx={{
                                gap: 1,
                                display: "flex",
                                alignItems: "end",
                                justifyContent: "space-between",
                            }}>
                                <Box width={"94%"}>
                                    <Autocomplete
                                        multiple
                                        data={groupList || []}
                                        defaultValue={dataModal?.groupIds}
                                        errorMessage={errorModal?.groupIds}
                                        placeholder={strings.format(strings.Staff.SELECT_WORK_GROUPS, (props.lableGroup || strings.Common.GROUP).toLowerCase())}
                                        label={Helpers.capitalizeFirstLetter(strings.format(strings.Staff.WORK_GROUPS, (props.lableGroup || strings.Common.GROUP).toLowerCase()))}
                                        onChange={(value: any) => {
                                            onChangeValue(value, "groupIds");
                                        }}
                                    />
                                </Box>
                                <Box width={"5%"} display={"flex"} justifyContent={"end"}>
                                    <Tooltip title={strings.Common.ADD_NEW}>
                                        <IconButton
                                            color="info"
                                            sx={{ padding: 0 }}
                                            onClick={() => { setOpenCreateGroup(true) }}
                                        >
                                            <AddCircleOutline />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Grid>
                    }
                </Grid>
            </Modal>

            {/* Create Quickly Group */}
            {openCreateGroup &&
                <ModalCreateUpdateGroup
                    lable={props.lableGroup}
                    openModal={openCreateGroup}
                    organizationId={props.organizationId}

                    hidenInputSelectGroupParent

                    onCloseModal={() => {
                        setOpenCreateGroup(false);
                    }}
                    onActionModal={(newdata) => {
                        if (!Helpers.isNullOrEmpty(newdata?.id)) {
                            setGroupList(prev => [
                                ...prev || [],
                                {
                                    code: newdata?.id || "",
                                    name: newdata?.name?.value?.[language] || "",
                                },
                            ]);
                        }
                        setOpenCreateGroup(false);

                        onChangeValue(newdata?.id, "groupId");
                    }}
                />
            }
        </>
    );
}

export default ModalInvitationMember;
